import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save } from 'lucide-react';
import { useAppState } from '../hooks/useAppState';
import { authApi, firmCatalogApi, type CatalogFirm } from '../lib/api';
import { RuleChangesFeed, markRuleChangesSeen, useRuleChanges } from '../components/guild/RuleChanges';
import type { PropFirm, PropFirmAccount } from '../types';
import { ICE, TEAL, ACCENT, FIELD, PANEL, WIN, LOSS, PX_FONT, TITLE_FONT, ON_ACCENT, PAGE, pixelFrame } from '../components/guild/theme';

/* Admin: keep each prop firm's rules current. Every save stamps "verified today" and is logged.
   The weekly rules check reports what changed on firms' sites; this is where an admin applies it. */

const label = { fontFamily: PX_FONT, fontSize: 10, letterSpacing: '0.12em', color: TEAL } as const;
const cell = { fontFamily: PX_FONT, fontSize: 13, color: ICE, background: FIELD, border: 'none', boxShadow: 'var(--pal-field-shadow)', padding: '6px 8px', width: '100%', minWidth: 0, borderRadius: 'var(--pal-radius, 0)' } as const;

const NUM_COLS: [keyof PropFirmAccount, string][] = [
  ['size', 'Size'], ['maxDrawdown', 'Max DD'], ['fundedMaxDrawdown', 'Funded DD'], ['dailyLossLimit', 'Daily loss'],
  ['profitTarget', 'Target'], ['maxContracts', 'Max contracts'], ['activationFee', 'Activation'],
];

export function FirmAdmin() {
  const navigate = useNavigate();
  const { user } = useAppState();
  const username = user?.discordUsername || 'trader';
  const [params, setParams] = useSearchParams();
  const [admin, setAdmin] = useState<boolean | null>(null);
  const [catalog, setCatalog] = useState<CatalogFirm[]>([]);
  const [draft, setDraft] = useState<PropFirm | null>(null);
  const [sources, setSources] = useState('');
  const [note, setNote] = useState('');
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);
  const selected = params.get('firm') ?? 'apex';
  const tab = params.get('tab') === 'changes' ? 'changes' : 'edit';
  const { log } = useRuleChanges(username, admin === true);
  useEffect(() => { if (tab === 'changes' && log.length) markRuleChangesSeen(); }, [tab, log.length]);

  useEffect(() => { authApi.me(username).then(me => setAdmin(me.roles.includes('admin'))).catch(() => setAdmin(false)); }, [username]);
  useEffect(() => { firmCatalogApi.list(username).then(setCatalog).catch(() => undefined); }, [username]);
  const entry = useMemo(() => catalog.find(c => c.id === selected) ?? catalog[0], [catalog, selected]);
  useEffect(() => {
    if (!entry) return;
    setDraft(structuredClone(entry.data)); setSources(entry.source_urls.join('\n')); setNote(''); setStatus(null);
  }, [entry]);

  const setAcct = (pi: number, ai: number, key: keyof PropFirmAccount, raw: string) => setDraft(d => {
    if (!d) return d;
    const n = structuredClone(d);
    const v = raw === '' ? (key === 'dailyLossLimit' || key === 'fundedMaxDrawdown' || key === 'maxContracts' ? null : 0) : Number(raw);
    (n.plans[pi].accounts[ai] as unknown as Record<string, unknown>)[key] = v === null ? (key === 'dailyLossLimit' ? null : undefined) : v;
    return n;
  });

  const save = async () => {
    if (!draft || !entry) return;
    setStatus(null);
    try {
      const firm = await firmCatalogApi.save(username, entry.id, {
        data: draft, source_urls: sources.split(/\s+/).filter(Boolean), verified_on: new Date().toISOString().slice(0, 10), note: note || undefined,
      });
      setCatalog(cs => cs.map(c => (c.id === firm.id ? firm : c)));
      setStatus({ ok: true, text: `Saved · verified today by ${firm.updated_by}` });
    } catch (e) { setStatus({ ok: false, text: e instanceof Error ? e.message : 'Save failed' }); }
  };

  if (admin === false) {
    return <div style={{ minHeight: '100vh', background: PAGE, padding: 40, color: ICE, fontFamily: PX_FONT }}>Firm rules can only be edited by admins.</div>;
  }

  return (
    <div style={{ minHeight: '100vh', background: PAGE }}>
      <main style={{ maxWidth: 1180, margin: '0 auto', padding: '32px 24px 64px', display: 'flex', flexDirection: 'column', gap: 22 }}>
        <button type="button" onClick={() => navigate('/hub?open=rx')} style={{ ...label, display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <ArrowLeft size={12} /> Prescription Engine
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
          <h1 className="pal-title" style={{ margin: 0, fontFamily: TITLE_FONT, fontWeight: 900, fontSize: 40 }}>FIRM <span className="pal-em">RULES</span></h1>
          <span style={{ display: 'flex', gap: 8 }}>
            {([['edit', 'Edit rules'], ['changes', `Change log (${log.length})`]] as const).map(([k, n]) => (
              <button key={k} type="button" onClick={() => setParams(k === 'changes' ? { tab: 'changes' } : { firm: selected })}
                style={{ fontFamily: PX_FONT, fontSize: 12, fontWeight: 700, padding: '8px 14px', border: 'none', cursor: 'pointer', borderRadius: 14,
                  background: tab === k ? ACCENT : 'transparent', color: tab === k ? ON_ACCENT : ICE, boxShadow: tab === k ? 'none' : 'inset 0 0 0 1px var(--pal-rim)' }}>{n}</button>
            ))}
          </span>
        </div>
        {tab === 'changes' ? <RuleChangesFeed log={log} /> : (

        <div style={{ display: 'grid', gridTemplateColumns: '240px minmax(0, 1fr)', gap: 22, alignItems: 'start' }}>
          {/* Firm list, with how fresh each one's rules are */}
          <nav style={{ background: PANEL, boxShadow: pixelFrame(), borderRadius: 'var(--pal-radius, 0)', padding: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {catalog.map(c => {
              const days = c.verified_on ? Math.floor((Date.now() - new Date(c.verified_on).getTime()) / 86400000) : null;
              const on = c.id === entry?.id;
              return (
                <button key={c.id} type="button" onClick={() => setParams({ firm: c.id })}
                  style={{ textAlign: 'left', padding: '10px 12px', border: 'none', cursor: 'pointer', borderRadius: 'var(--pal-radius, 0)', background: on ? 'var(--pal-tint-accent)' : 'none', boxShadow: on ? `inset 3px 0 0 ${ACCENT}` : 'none' }}>
                  <div style={{ fontFamily: PX_FONT, fontSize: 13, fontWeight: 700, color: ICE }}>{c.data.name}</div>
                  <div style={{ ...label, fontSize: 9, color: days !== null && days > 60 ? LOSS : TEAL }}>
                    {c.data.market} · {days === null ? 'never verified' : days === 0 ? 'verified today' : `verified ${days}d ago`}
                  </div>
                </button>
              );
            })}
          </nav>

          {draft && entry && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{ background: PANEL, boxShadow: pixelFrame(), borderRadius: 'var(--pal-radius, 0)', padding: 18, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
                <label><div style={label}>Firm name</div><input style={cell} value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} /></label>
                <label><div style={label}>Max funded accounts</div><input style={cell} inputMode="numeric" value={draft.maxFundedAccounts ?? ''} onChange={e => setDraft({ ...draft, maxFundedAccounts: e.target.value ? Number(e.target.value) : null })} /></label>
                <label style={{ gridColumn: '1 / -1' }}><div style={label}>Rule sources (one URL per line; the weekly check reads these)</div>
                  <textarea style={{ ...cell, minHeight: 56, resize: 'vertical' }} value={sources} onChange={e => setSources(e.target.value)} /></label>
              </div>

              {draft.plans.map((p, pi) => (
                <div key={p.id} style={{ background: PANEL, boxShadow: pixelFrame(), borderRadius: 'var(--pal-radius, 0)', padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end' }}>
                    <label style={{ flex: '1 1 180px' }}><div style={label}>Plan</div>
                      <input style={{ ...cell, fontWeight: 700 }} value={p.name} onChange={e => setDraft(d => { const n = structuredClone(d!); n.plans[pi].name = e.target.value; return n; })} /></label>
                    <label><div style={label}>Drawdown type</div>
                      <select style={cell} value={p.drawdownType} onChange={e => setDraft(d => { const n = structuredClone(d!); n.plans[pi].drawdownType = e.target.value as 'trailing'; return n; })}>
                        <option value="trailing">trailing</option><option value="eod">eod</option><option value="static">static</option>
                      </select></label>
                    <label><div style={label}>Funded consistency %</div>
                      <input style={{ ...cell, width: 90 }} inputMode="decimal" value={p.fundedConsistencyRulePct === null ? '' : Math.round(p.fundedConsistencyRulePct * 100)}
                        onChange={e => setDraft(d => { const n = structuredClone(d!); n.plans[pi].fundedConsistencyRulePct = e.target.value ? Number(e.target.value) / 100 : null; return n; })} /></label>
                    <label><div style={label}>Payout split %</div>
                      <input style={{ ...cell, width: 80 }} inputMode="decimal" value={Math.round(p.payoutSplit * 100)}
                        onChange={e => setDraft(d => { const n = structuredClone(d!); n.plans[pi].payoutSplit = Number(e.target.value) / 100; return n; })} /></label>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '6px 6px' }}>
                      <thead><tr>{NUM_COLS.map(([, n]) => <th key={n} style={{ ...label, textAlign: 'left', fontWeight: 400 }}>{n}</th>)}<th /></tr></thead>
                      <tbody>
                        {p.accounts.map((a, ai) => (
                          <tr key={ai}>
                            {NUM_COLS.map(([k]) => (
                              <td key={k}><input style={cell} inputMode="decimal" value={(a[k] as number | null | undefined) ?? ''} placeholder="—" onChange={e => setAcct(pi, ai, k, e.target.value)} /></td>
                            ))}
                            <td><button type="button" aria-label="Remove size" onClick={() => setDraft(d => { const n = structuredClone(d!); n.plans[pi].accounts.splice(ai, 1); return n; })}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: LOSS }}><Trash2 size={14} /></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button type="button" onClick={() => setDraft(d => { const n = structuredClone(d!); n.plans[pi].accounts.push({ size: 0, profitTarget: 0, maxDrawdown: 0, dailyLossLimit: null, activationFee: 0 }); return n; })}
                    style={{ ...label, display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: ACCENT, alignSelf: 'flex-start', padding: 0 }}>
                    <Plus size={13} /> Add account size
                  </button>
                </div>
              ))}

              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <input style={{ ...cell, flex: '1 1 320px' }} placeholder="What changed? (logged with the edit)" value={note} onChange={e => setNote(e.target.value)} />
                <button type="button" onClick={save}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', border: 'none', cursor: 'pointer', background: ACCENT, color: ON_ACCENT, fontFamily: PX_FONT, fontWeight: 800, fontSize: 13, borderRadius: 'var(--pal-radius, 0)' }}>
                  <Save size={14} /> Save &amp; mark verified
                </button>
                {status && <span style={{ ...label, color: status.ok ? WIN : LOSS }}>{status.text}</span>}
              </div>
              <div style={label}>Last updated by {entry.updated_by ?? '—'}{entry.verified_on ? ` · verified ${entry.verified_on}` : ''}</div>
            </div>
          )}
        </div>
        )}
      </main>
    </div>
  );
}
